/**
 * T742 slice 3 — THE BACKSTOP. Pure decision logic for "may this URL leave for a person?".
 *
 * ADR-0025 § *Alternatives* keeps a runtime assertion behind the seam, and names exactly what
 * it is for:
 *
 *   "Catches composition, but a system that only fails at runtime teaches nobody at write
 *    time. Kept as the backstop behind the seam."
 *
 * ── What the write-time gates cannot see ─────────────────────────────────────────────
 *
 * `lint-host-ssot` and `lint-delivery-host-ssot` scan SOURCE for a host LITERAL. A URL
 * assembled at runtime from a config value — `` `${env.R2_S3_ENDPOINT}/${key}?X-Amz-…` `` —
 * contains no literal, so both gates read it as clean. That is not a hypothetical: it is
 * precisely how the presigned guide URL reached four surfaces and a woman's inbox on
 * 2026-09-13 while every gate in this repo was green.
 *
 * So this module answers the question the scanners cannot: given the URLs a response is
 * actually about to hand over, are they on a host we have declared and the monitor watches?
 *
 * ── Why it is pure, and why it has no imports ────────────────────────────────────────
 *
 * Its one caller is `functions/[[path]].ts`, the Pages catch-all — the single place EVERY
 * response from this project passes through, including the loader/action redirects that
 * `entry.server.tsx` explicitly does not cover. That file is built by wrangler without the
 * `~/` tsconfig path, so anything it reaches must resolve relatively; keeping this module
 * import-free makes that a non-issue and makes every rule here testable with no fixture.
 *
 * `isDeliverableUrl` is ALSO what `~/lib/delivery-url.server` checks its own output with, so
 * the seam and the backstop cannot disagree about what "our zone" means. Two definitions of
 * one security rule is the shape of defect this whole ticket exists to remove.
 */

export interface EgressCandidate {
  /** Where in the response the URL was about to leave from. */
  where: "location" | "body";
  url: string;
}

/**
 * Is this a URL we are willing to hand a person?
 *
 * A root-relative path always is — it resolves on whatever host she already reached, which is
 * the strongest form of "our zone" because it names no host at all. An absolute URL must be
 * `https:` and name a declared delivery host, or a per-PR preview, which no fixed declaration
 * can enumerate.
 *
 * `//evil.example` is refused explicitly: it reads as root-relative to a careless check and as
 * a full cross-origin URL to a browser, and that gap is the reason this is a function rather
 * than a `startsWith("/")` repeated at every call site.
 */
/**
 * TWO probe origins, used only to ask the URL parser a question. Nothing is ever fetched from
 * either; `.invalid` is reserved by RFC 2606 and resolves nowhere.
 *
 * **Why two and not one.** With a single probe, the probe's own host is a valid answer:
 * `//same-origin.invalid/x` resolves to that origin, so it passed both conditions while a
 * browser would send a member to the host `same-origin.invalid` — `ERR_NAME_NOT_RESOLVED`,
 * which is the member-facing symptom this whole ticket exists to prevent. Found by AI review
 * round 4, on the fix for round 3.
 *
 * A value that resolves same-origin against BOTH cannot be naming a host, because it would
 * have to name two different ones at once. That keeps the "ask the parser" property intact
 * rather than bolting a `//` special case onto it — which would be enumeration again, the
 * exact mistake the previous two rounds were about.
 */
const SAME_ORIGIN_PROBES = ["https://probe-a.invalid", "https://probe-b.invalid"] as const;

/**
 * Is this value a path that stays on the origin the page is already on?
 *
 * ── Why it ASKS THE PARSER instead of enumerating the tricks (T748) ──────────────────
 *
 * Until 2026-09-14 the rule was "begins with a slash, but not with two", written out three
 * times. It refused only one of the forms a browser treats as protocol-relative, and the
 * hunt for the rest went two rounds:
 *
 *   `/\evil.example.com/x`    — the parser normalises `\` to `/`   (the ticket)
 *   `/<TAB>/evil.example.com/x`  — the parser STRIPS tab, LF and CR   (AI review round 3)
 *   `/<LF>/evil.example.com/x`   — anywhere in the input, before parsing
 *   `/<CR>/evil.example.com/x`
 *   `//same-origin.invalid/x`    — the probe's own host, against a single probe (round 4)
 *
 * All four pass a prefix test and all four resolve to `evil.example.com` — verified against
 * the platform's own parser, not argued. A fifth normalisation rule would have found the
 * same hole, which is the real lesson: **a predicate that enumerates a parser's quirks is
 * always one rule behind the parser.**
 *
 * So this one does not enumerate. It hands the value to the same parser a browser uses and
 * asks where it lands. Two conditions, and both are needed:
 *
 *  1. **resolved against TWO probe origins, it stays on each of them** — which is exactly the
 *     question "can this navigate off our host?", answered by the thing that decides it.
 *     This is what makes the check immune to the next normalisation rule rather than
 *     patched against the last one. Two probes rather than one because a single probe's own
 *     host is a valid answer to it; see `SAME_ORIGIN_PROBES`.
 *  2. **with the characters the parser strips removed, it is still an absolute path** —
 *     because a bare `foo` also resolves same-origin and is not what any caller here means
 *     by a path. The seam emits `/es/descarga?t=…`; nothing emits a relative segment.
 *
 * **Encoded forms (`%5C`, `%2F`) are still out of scope, and now for a sharper reason than
 * before:** they are not normalisation, they are content. The parser does not decode them
 * either, so `/%5Cevil.example.com` stays a path on our own origin — in a browser as well as
 * here. The two agree, which is the property this function is built on.
 */
export function isSameOriginPath(value: string): boolean {
  try {
    for (const probe of SAME_ORIGIN_PROBES) {
      if (new URL(value, probe).origin !== probe) return false;
    }
  } catch {
    return false;
  }
  return value.replace(/[\t\n\r]/g, "").startsWith("/");
}

export function isDeliverableUrl(
  url: string,
  allowedHosts: readonly string[],
  previewHostSuffix: string,
): boolean {
  if (isSameOriginPath(url)) return true;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:") return false;
  const host = parsed.hostname.toLowerCase();
  if (previewHostSuffix && host.endsWith(previewHostSuffix)) return true;
  return allowedHosts.includes(host);
}

/**
 * Every absolute `http(s)` URL a JSON body names.
 *
 * Deliberately a scan of the TEXT rather than a walk of the parsed object: the field carrying
 * the URL has a different name on every endpoint (`pdf_url`, `videoUrl`, `imageUrl`,
 * `coverUrl`, `thumbnailUrl`…), and a backstop that has to know the field names is a list
 * somebody maintains — which is the thing ADR-0025 decision 2 refuses. The text does not
 * care what the key is called.
 *
 * The trailing-character trim matters: a URL inside JSON is followed by `"`, and `\/` is how
 * some encoders escape a slash.
 */
export function absoluteUrlsIn(text: string): string[] {
  const matches = text.replace(/\\\//g, "/").match(/https?:\/\/[^"'\s\\<>)\]}]+/g) ?? [];
  return [...new Set(matches)];
}

/**
 * The URL with its query string removed, for a log line or an error body.
 *
 * A finding names a HOST; the query is never part of the answer and is frequently a bearer
 * credential — a presigned R2 URL carries `X-Amz-Signature`, and `/es/descarga?t=…` carries a
 * 90-day download token. Writing either into a Worker log puts a capability at rest, and the
 * non-production 500 body hands it back over the wire. The host and the path say everything a
 * reader needs; the secret half says nothing and costs something.
 *
 * A value that is not a URL (a path, or a malformed string) keeps everything before the first
 * `?`, so the same rule holds without a second code path.
 */
export function redactUrlForReport(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url.split("?")[0];
  }
}

/**
 * The URLs in this response that name a host outside the declared delivery set.
 *
 * `locationHeader` is checked on every response it exists on — it is one header read, it is
 * free, and it is the exact shape T741 was: `/tu-guia` answered on our own domain and then
 * `302`'d to `*.r2.cloudflarestorage.com`.
 *
 * `jsonBody` is optional because the caller only buffers a body when it is small and declared
 * `application/json`. A PDF, a video or an SSR document is never read here: buffering those
 * would break streaming for the sake of a check, and it is the API responses that carry
 * person-facing URLs anyway (`pdf_url` is what `/api/leads` hands the browser).
 */
export function findEgressViolations(input: {
  locationHeader?: string | null;
  jsonBody?: string | null;
  allowedHosts: readonly string[];
  previewHostSuffix: string;
}): EgressCandidate[] {
  const { allowedHosts, previewHostSuffix } = input;
  const out: EgressCandidate[] = [];

  const location = input.locationHeader?.trim();
  if (location && !isDeliverableUrl(location, allowedHosts, previewHostSuffix)) {
    out.push({ where: "location", url: location });
  }

  if (input.jsonBody) {
    for (const url of absoluteUrlsIn(input.jsonBody)) {
      if (!isDeliverableUrl(url, allowedHosts, previewHostSuffix)) {
        out.push({ where: "body", url });
      }
    }
  }

  return out;
}

/**
 * Request paths the backstop does not judge, each with the reason it cannot.
 *
 * This is the one list in slice 3 that is maintained by hand, so it is kept as short as the
 * facts allow and every entry says what would otherwise break. A path added here to silence a
 * finding rather than to state a fact is the failure mode; the entries below are all cases
 * where an off-zone URL is the CORRECT answer.
 */
export const EGRESS_EXEMPT_PATH_PREFIXES: ReadonlyArray<{ prefix: string; reason: string }> = [
  {
    prefix: "/api/auth/",
    reason:
      "better-auth's social sign-in redirects the browser to the identity provider by design " +
      "(`socialProviders.google` in auth.server.ts). An off-zone Location here is the feature, " +
      "not a defect — and it is a host the member chose, not one we picked to deliver bytes from.",
  },
  {
    prefix: "/admin/",
    reason:
      "the Back Office is the operator, not a member being delivered to. It legitimately " +
      "receives presigned UPLOAD URLs on the R2 S3 endpoint (`admin.media.presign`), which " +
      "ADR-0025 § *Out of scope* excludes by name: a block there is an operator problem, and " +
      "an alarm nobody can act on is how a monitor stops being read.",
  },
  {
    prefix: "/api/admin/",
    reason:
      "the JSON half of the Back Office, for the same reason as `/admin/` above — and the half " +
      "that actually carries the presigned PUT URL in a response body, so excluding the screen " +
      "without this one would exempt the page and judge the request it makes.",
  },
];

/**
 * Is this path one the backstop deliberately does not judge?
 *
 * Every prefix ends in `/` and the path itself is accepted as an exact match, so an exemption
 * covers a SEGMENT rather than a string. A bare `startsWith("/admin")` would silently exempt a
 * future member-facing `/administracion-…`, and an over-broad exemption in a gate is worse than
 * no gate: it is a gate that reports clean about the thing it stopped looking at.
 */
export function isEgressExemptPath(pathname: string): boolean {
  return EGRESS_EXEMPT_PATH_PREFIXES.some(
    (e) => pathname.startsWith(e.prefix) || pathname === e.prefix.replace(/\/$/, ""),
  );
}

/**
 * The largest JSON body this guard will read, in bytes.
 *
 * A cap, not a policy: the check is a regex over text and costs microseconds, but reading a
 * body means holding it, and a Back Office list response can be megabytes. 256 KB covers every
 * member-facing API response this app produces by two orders of magnitude — `/api/lead-magnet`,
 * one of the two that actually carries a person-facing URL, answers in 241 bytes (measured on
 * the deployed preview, 2026-09-14). Past the cap the body is left UNSCANNED rather than
 * truncated, because a partial scan that reports clean is worse than no scan.
 */
export const EGRESS_BODY_SCAN_MAX_BYTES = 256 * 1024;

/**
 * The body as text, or `null` when it is larger than `maxBytes`.
 *
 * ── Why this counts bytes instead of trusting `content-length` ───────────────────────
 *
 * The first version gated the read on that header, and AI review round 1 caught what that
 * actually does: a Workers JSON response is frequently chunked and carries no
 * `content-length`, so `Number(null ?? "0")` read as ZERO — under the cap — and the whole
 * stream was buffered anyway. The documented limit did not hold for exactly the responses
 * most likely to be large, and the test that "proved" the cap set the header by hand, so it
 * never touched that path.
 *
 * Skipping a body with no `content-length` would have been the other easy answer and is worse:
 * it turns the guard off for an unknown share of real traffic and says nothing — the shape of
 * failure this whole ticket is about. So the header is not consulted at all. The stream is
 * read chunk by chunk, the count is real, and the read is abandoned the moment it exceeds the
 * cap.
 */
export async function readBodyUpTo(response: Response, maxBytes: number): Promise<string | null> {
  const body = response.clone().body;
  if (!body) return null;
  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    // The result object is kept whole rather than destructured: `ReadableStreamReadResult` is a
    // discriminated union, so `result.value` narrows to a chunk once `result.done` is false.
    // Destructuring loses that and forces a `!value` branch no runtime ever takes — untestable
    // defensive code, which a 100%-branch floor is exactly the right instrument to refuse.
    let result = await reader.read();
    while (!result.done) {
      const value = result.value;
      total += value.byteLength;
      if (total > maxBytes) {
        // NOT awaited. `clone()` tees the stream, and awaiting `cancel()` on one branch while
        // the other is still unread blocks until the other side drains — which, for the
        // original response, is the member's browser. Measured: it hung the test at 15 s.
        void reader.cancel().catch(() => {});
        return null;
      }
      chunks.push(value);
      result = await reader.read();
    }
  } catch {
    // A body that cannot be read is not a violation — it is a body we cannot judge.
    return null;
  }
  const joined = new Uint8Array(total);
  let at = 0;
  for (const chunk of chunks) {
    joined.set(chunk, at);
    at += chunk.byteLength;
  }
  return new TextDecoder().decode(joined);
}

/**
 * Run one outgoing response past the backstop.
 *
 * ── What it reads, and what it refuses to read ──────────────────────────────────────
 *
 * The `Location` header, always: one header read, free, and the precise shape of T741 —
 * `/tu-guia` answered on our own domain and then `302`'d to a host Spanish networks could not
 * reach. A JSON body, only when it is declared `application/json` and under the cap. A PDF, a
 * video and an SSR stream are never touched: buffering those to check them would break
 * streaming for every member to catch a class of bug that does not live in them.
 *
 * ── What it does when it finds one ──────────────────────────────────────────────────
 *
 * **In production it reports and lets the response through.** A member mid-flow is not the
 * right person to pay for our gate being wrong, and the value in production is visibility:
 * `ERR_CONNECTION_FAILED` never reaches our logs (ADR-0025 § *The observability constraint*),
 * which is why T741 waited for a founder with a phone to find it.
 *
 * **Everywhere else it fails the request**, so a preview deploy goes red and the smoke E2E that
 * runs against it says so on the PR. That is the half that makes this a gate rather than a
 * dashboard.
 *
 * Every failure inside the check is swallowed and the original response returned: a backstop
 * that breaks the thing it guards has inverted its own purpose.
 */
export async function guardResponseEgress(
  request: Request,
  response: Response,
  opts: {
    allowedHosts: readonly string[];
    previewHostSuffix: string;
    isProduction: boolean;
    report?: (message: string) => void;
  },
): Promise<Response> {
  try {
    const { pathname } = new URL(request.url);
    if (isEgressExemptPath(pathname)) return response;

    // The content type is the whole decision about WHETHER to read: a PDF, a video and an SSR
    // stream are never touched, because buffering those to check them would break streaming
    // for every member to catch a class of bug that does not live in them.
    const contentType = response.headers.get("content-type") ?? "";
    const jsonBody = contentType.includes("application/json")
      ? // Reads the CLONE, and only up to the cap. The original body must stay unconsumed —
        // it is what the member gets.
        await readBodyUpTo(response, EGRESS_BODY_SCAN_MAX_BYTES)
      : null;

    const violations = findEgressViolations({
      locationHeader: response.headers.get("location"),
      jsonBody,
      allowedHosts: opts.allowedHosts,
      previewHostSuffix: opts.previewHostSuffix,
    });
    if (violations.length === 0) return response;

    // Query strings are stripped: a finding names a host, and the query is frequently a bearer
    // credential (AI review round 1). See `redactUrlForReport`.
    const detail = violations.map((v) => `${v.where}:${redactUrlForReport(v.url)}`).join(" | ");
    (opts.report ?? ((m: string) => console.error(m)))(
      `[egress] ${response.status} ${pathname} hands over ${violations.length} URL(s) outside ` +
        `the declared delivery set — ${detail}`,
    );
    if (opts.isProduction) return response;

    return new Response(
      `Egress violation (T742 R-T742-4): ${detail}\n\n` +
        `This response names a host that is not in the declared delivery set, so nothing\n` +
        `monitors whether a member can reach it — the T741 failure shape. Resolve the URL\n` +
        `through app/lib/delivery-url.server.ts, or declare the host in its owner\n` +
        `(app/config/hosts.ts or app/config/external-origins.ts) if it genuinely delivers.\n`,
      { status: 500, headers: { "content-type": "text/plain; charset=utf-8" } },
    );
  } catch {
    // A backstop that breaks the thing it guards has inverted its own purpose.
    return response;
  }
}
